import { visibleCategory } from './finance';
import { normalizeText } from './importer';
import { addMonths } from './period';
import type { DailyRevenue, DeliveryPlatform, ExpenseEntry } from './types';

/**
 * «Este mes vendiste X por Uber Eats y no hay factura de comisiones»
 * (revisión de Alex del 14 oct 2026, sección 8).
 *
 * Hasta que exista el cálculo automático de comisiones, si alguien no sube la
 * factura de la app su resultado sale más alto de lo real y no se entera.
 * Este aviso lo dice: por cada app configurada con ventas ese mes y sin un
 * gasto de «Comisiones (tarjeta y apps)» de esa app.
 *
 * Qué cuenta como «la factura de comisiones de esa app»: un gasto de la
 * categoría de comisiones (también con su clave vieja) cuyo proveedor o
 * concepto nombra la app («Uber», «Uber Eats»), fechado ese mes o el
 * siguiente (las apps suelen facturar el mes ya cerrado). Uno que habla de
 * las apps sin nombrar ninguna («Comisiones de plataformas») vale para todas.
 * Una comisión del banco o de la tarjeta («Comisiones TPV») no cuenta.
 */

export interface AppSinComisiones {
  platformId: string;
  nombre: string;
  /** Lo vendido ese mes por esa app (como se anota: con IVA, lo que cobra la app). */
  vendido: number;
}

const GENERICAS = /\b(plataforma|plataformas|apps?|delivery|domicilio|reparto)\b/;

/** Las palabras con las que un gasto puede nombrar la app: «uber eats» y «uber». */
function palabrasDeApp(nombre: string): string[] {
  const n = normalizeText(nombre);
  if (!n) return [];
  const primera = n.split(' ')[0];
  return primera.length >= 4 && primera !== n ? [n, primera] : [n];
}

const nombra = (texto: string, palabra: string) => new RegExp(`(?:^| )${palabra.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?: |$)`).test(texto);

export function appsSinFacturaDeComisiones(input: {
  month: string;
  revenues: Array<Pick<DailyRevenue, 'date' | 'delivery'>>;
  platforms: Array<Pick<DeliveryPlatform, 'platformId' | 'name'>> | undefined;
  expenses: Array<Pick<ExpenseEntry, 'date' | 'category' | 'concept' | 'supplierId' | 'porConfirmar'>>;
  /** Nombre de cada proveedor, para leer «Uber Portier B.V.». */
  supplierName?: (supplierId: string) => string | undefined;
}): AppSinComisiones[] {
  const { month } = input;
  const platforms = input.platforms ?? [];
  if (platforms.length === 0) return [];
  const vendido = new Map<string, number>();
  for (const r of input.revenues) {
    if (!r.date.startsWith(month)) continue;
    for (const [id, v] of Object.entries(r.delivery ?? {})) {
      if (Number.isFinite(v) && v > 0) vendido.set(id, (vendido.get(id) ?? 0) + v);
    }
  }
  const siguiente = addMonths(month, 1);
  const textos = input.expenses
    .filter((e) => !e.porConfirmar && visibleCategory(e.category) === 'comisiones_bancarias')
    .filter((e) => e.date.startsWith(month) || e.date.startsWith(siguiente))
    .map((e) => normalizeText(`${e.supplierId ? input.supplierName?.(e.supplierId) ?? '' : ''} ${e.concept ?? ''}`));
  const nombranAlguna = (t: string) => platforms.some((p) => palabrasDeApp(p.name).some((w) => nombra(t, w)));
  const deTodas = textos.some((t) => GENERICAS.test(t) && !nombranAlguna(t));
  if (deTodas) return [];
  const out: AppSinComisiones[] = [];
  for (const p of platforms) {
    const v = Math.round((vendido.get(p.platformId) ?? 0) * 100) / 100;
    if (!(v > 0)) continue;
    const palabras = palabrasDeApp(p.name);
    if (palabras.length === 0) continue;
    if (textos.some((t) => palabras.some((w) => nombra(t, w)))) continue;
    out.push({ platformId: p.platformId, nombre: p.name, vendido: v });
  }
  return out.sort((a, b) => b.vendido - a.vendido);
}

/** «Este mes vendiste 1.234,00 € por Uber Eats y no hay factura de comisiones. Súbela o tu resultado sale más alto de lo real.» */
export function textoAppSinComisiones(app: AppSinComisiones, money: (n: number) => string, mes = 'Este mes'): string {
  return `${mes} vendiste ${money(app.vendido)} por ${app.nombre} y no hay factura de comisiones. Súbela o tu resultado sale más alto de lo real.`;
}
